import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Alert, AlertIcon, Box, Button, Divider, FormControl, FormLabel,
  HStack, Heading, Icon, Image, Input, Stack, Text,
} from '@chakra-ui/react';
import { useRegisterMutation, useLoginMutation } from '../redux/services/auth';
import { setAuthenticated } from '../redux/features/authSlice';
import { useDispatch } from 'react-redux';
import { readReferralCode, writeReferralCode } from '../referrals/referralStore';
import { attributeIfPending } from '../referrals/referralApi';

const mainServiceUrl = import.meta.env.VITE_MAIN_SERVICE_URL || '';
const defaultCompanyID =
  import.meta.env.VITE_MAIN_SERVICE_DEFAULT_COMPANY_ID || '5355504552555345522d2d2d';
const FREE_SIGNUP_CREDITS = Number(import.meta.env.VITE_FREE_SIGNUP_CREDITS || 5);

function GoogleGlyph() {
  return (
    <Icon viewBox="0 0 48 48" boxSize="20px">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29 35.3 26.6 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z" />
    </Icon>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [loginMutation, { isLoading: isLoggingIn }] = useLoginMutation();
  const [error, setError] = useState<string | null>(null);
  const isLoading = isRegistering || isLoggingIn;
  const nextParam = searchParams.get('next');
  const loginHref = `/login${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ''}`;
  // Referral code — pre-fill from localStorage (URL-based capture wrote
  // it there in App.tsx on very first module load). The field stays
  // hidden until the user opens "Have an invite code?" so the primary
  // signup flow doesn't gain any extra friction for the 95% of users
  // who arrive without a code.
  const [showInvite, setShowInvite] = useState<boolean>(() => !!readReferralCode());
  const [inviteCode, setInviteCode] = useState<string>(() => readReferralCode() || '');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // If the user typed a code manually, save it BEFORE register+login so
    // attributeIfPending() (below) finds it after the verify call succeeds.
    // URL-based captures already landed in localStorage on App.tsx load.
    if (inviteCode.trim()) writeReferralCode(inviteCode);

    try {
      await register({ email, password, companyID: defaultCompanyID }).unwrap();
      await loginMutation({ email, password, rememberMe: false }).unwrap();
      const verifyRes = await fetch(`${mainServiceUrl}/auth/verify`, { credentials: 'include' });
      if (verifyRes.ok) {
        const verifyData = await verifyRes.json();
        dispatch(setAuthenticated({ user: verifyData.user || {} }));
        // Fire-and-forget referral attribution. Must NOT block navigation —
        // signup succeeded, referral is a nice-to-have. The helper reads
        // the code from localStorage, POSTs, and clears the store.
        const uid = verifyData?.user?.id || verifyData?.user?._id;
        if (uid) void attributeIfPending(String(uid));
      }
      navigate(nextParam || '/generator');
    } catch (err: any) {
      setError(err?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const handleGoogleSignup = () => {
    const socialUrl = new URL(`${mainServiceUrl}/auth/cognito/social/google`);
    socialUrl.searchParams.set('mode', 'signup');
    if (nextParam) socialUrl.searchParams.set('next', nextParam);
    window.location.href = socialUrl.toString();
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="stretch"
      justifyContent="center"
      bgGradient="linear(180deg, #0F0B1F 0%, #1A0F2E 40%, #2E1240 100%)"
      color="whiteAlpha.900"
      px={{ base: 5, md: 8 }}
      py={{ base: 8, md: 12 }}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="-140px"
        left="50%"
        transform="translateX(-50%)"
        w="520px"
        h="520px"
        bg="radial-gradient(circle, rgba(233,74,143,0.32) 0%, rgba(233,74,143,0) 65%)"
        pointerEvents="none"
        aria-hidden
      />
      <Box
        position="absolute"
        bottom="-180px"
        right="-140px"
        w="480px"
        h="480px"
        bg="radial-gradient(circle, rgba(115,84,255,0.28) 0%, rgba(115,84,255,0) 65%)"
        pointerEvents="none"
        aria-hidden
      />

      <Stack w="full" maxW="440px" spacing={7} justify="center" position="relative" zIndex={1}>
        <Stack spacing={4} align="center" textAlign="center">
          <Image src="/logo/logo_single.png" alt="MixGenie" boxSize="64px" borderRadius="14px" />
          <Heading
            as="h1"
            fontSize={{ base: '30px', md: '36px' }}
            fontWeight={800}
            lineHeight="1.1"
            letterSpacing="-0.01em"
            style={{ textWrap: 'balance' as any }}
          >
            Start creating in a minute
          </Heading>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="whiteAlpha.700" maxW="360px">
            {FREE_SIGNUP_CREDITS} free song credits when you sign up. No card required.
          </Text>
        </Stack>

        <Stack
          spacing={4}
          bg="rgba(20, 14, 36, 0.7)"
          backdropFilter="blur(18px)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="20px"
          p={{ base: 6, md: 7 }}
          boxShadow="0 24px 60px rgba(0,0,0,0.45)"
        >
          <Button
            onClick={handleGoogleSignup}
            leftIcon={<GoogleGlyph />}
            size="lg"
            w="full"
            bg="white"
            color="#3c4043"
            fontWeight={600}
            _hover={{ bg: 'gray.100' }}
            _active={{ bg: 'gray.200' }}
            borderRadius="12px"
            data-cta="auth:google-signup"
          >
            Sign up with Google
          </Button>

          <HStack>
            <Divider borderColor="whiteAlpha.200" />
            <Text fontSize="xs" color="whiteAlpha.500" letterSpacing="0.14em" textTransform="uppercase" whiteSpace="nowrap">
              or with email
            </Text>
            <Divider borderColor="whiteAlpha.200" />
          </HStack>

          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {error && (
                <Alert status="error" borderRadius="10px" bg="red.900" color="white" border="1px solid" borderColor="red.600">
                  <AlertIcon color="red.200" />
                  {error}
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                  Email
                </FormLabel>
                <Input
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  color="whiteAlpha.900"
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                  _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                  _placeholder={{ color: 'whiteAlpha.400' }}
                  borderRadius="12px"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                  Password
                </FormLabel>
                <Input
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  color="whiteAlpha.900"
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                  _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                  _placeholder={{ color: 'whiteAlpha.400' }}
                  borderRadius="12px"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                  Confirm password
                </FormLabel>
                <Input
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  color="whiteAlpha.900"
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                  _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                  _placeholder={{ color: 'whiteAlpha.400' }}
                  borderRadius="12px"
                />
              </FormControl>

              {/* Invite code — collapsed by default so the primary signup
                  flow stays uncluttered. Auto-expands when the user
                  arrived with a `?ref=` code (pre-filled), so they can
                  see the code they'll be credited with. */}
              {showInvite ? (
                <FormControl>
                  <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                    Invite code (optional)
                  </FormLabel>
                  <Input
                    name="referralCode"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="M4P2XQ"
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="whiteAlpha.900"
                    fontFamily="ui-monospace, SF Mono, Menlo, monospace"
                    letterSpacing="0.1em"
                    _hover={{ borderColor: 'whiteAlpha.400' }}
                    _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                    _placeholder={{ color: 'whiteAlpha.400' }}
                    borderRadius="12px"
                  />
                </FormControl>
              ) : (
                <Text
                  as="button"
                  type="button"
                  onClick={() => setShowInvite(true)}
                  fontSize="xs"
                  color="whiteAlpha.500"
                  alignSelf="flex-start"
                  _hover={{ color: 'whiteAlpha.800', textDecoration: 'underline' }}
                >
                  Have an invite code?
                </Text>
              )}

              <Button
                type="submit"
                isLoading={isLoading}
                w="full"
                size="lg"
                borderRadius="12px"
                bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
                color="white"
                fontWeight={700}
                _hover={{ bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)', transform: 'translateY(-1px)' }}
                _active={{ transform: 'translateY(0)' }}
                boxShadow="0 10px 24px rgba(233,74,143,0.35)"
                data-cta="auth:sign-up"
              >
                Create account
              </Button>
            </Stack>
          </form>
        </Stack>

        <Text fontSize="sm" textAlign="center" color="whiteAlpha.700">
          Already have an account?{' '}
          <Link to={loginHref}>
            <Text as="span" color="pink.300" fontWeight={600} _hover={{ textDecoration: 'underline' }}>
              Sign in
            </Text>
          </Link>
        </Text>
      </Stack>
    </Box>
  );
}
